import { useEffect, useState } from "react";
import { api } from "../../api.js";
import {
  Badge, Button, EmptyState, Eyebrow, LoadingRows, Notice, Panel, Table, TableCell,
  TableHead, TableHeader, TableRow,
} from "../../components/ui.jsx";
import { formatDate, formatValue } from "./utils.js";

export default function CompanyDetailPanel({ id, onClose }) {
  const [state, setState] = useState({ loading: true, company: null, error: "" });

  useEffect(() => {
    let active = true;
    setState({ loading: true, company: null, error: "" });
    api.platformCatalogCompany(id)
      .then((data) => active && setState({ loading: false, company: data.company, error: "" }))
      .catch((error) => active && setState({ loading: false, company: null, error: error.message }));
    return () => { active = false; };
  }, [id]);

  if (state.loading) return <Panel><LoadingRows count={4} /></Panel>;
  if (state.error) return <Notice>{state.error}</Notice>;
  const company = state.company;

  return (
    <Panel className="grid gap-7">
      <div className="flex items-start justify-between gap-4">
        <div className="grid min-w-0 gap-1">
          <Eyebrow>Catalog record</Eyebrow>
          <h2 className="truncate text-xl font-bold tracking-[-0.02em]">{company.name}</h2>
          <p className="text-xs text-muted-foreground">{company.domain}</p>
        </div>
        <Button variant="ghost" onClick={onClose}>Close</Button>
      </div>
      <div className="grid gap-5 lg:grid-cols-2">
        <section>
          <h3 className="mb-3 text-sm font-semibold text-foreground">Current values</h3>
          <div className="divide-y divide-neutral-100 border-y border-neutral-100">
            {company.fields.map((field) => (
              <div className="grid gap-1 py-3 sm:grid-cols-[150px_1fr_auto] sm:items-center" key={field.id}>
                <span className="text-xs text-muted-foreground">{field.label}</span>
                <strong className="min-w-0 break-words text-xs">{formatValue(field.current?.value)}</strong>
                {field.current && <Badge status={field.current.freshness}>{field.current.provider}</Badge>}
              </div>
            ))}
          </div>
        </section>
        <section>
          <h3 className="mb-3 text-sm font-semibold text-foreground">Used by workspaces</h3>
          <div className="divide-y divide-neutral-100 border-y border-neutral-100">
            {company.workspaces.length ? company.workspaces.map((workspace) => (
              <div className="flex min-h-12 items-center justify-between gap-3 py-2" key={workspace.id}>
                <span className="grid min-w-0">
                  <strong className="truncate text-xs">{workspace.name}</strong>
                  <small className="text-[11px] text-muted-foreground">{workspace.slug}</small>
                </span>
                <Badge>{workspace.company_records} record{workspace.company_records === 1 ? "" : "s"}</Badge>
              </div>
            )) : <p className="py-4 text-xs text-muted-foreground">No linked workspace records.</p>}
          </div>
        </section>
      </div>
      <section>
        <h3 className="mb-3 text-sm font-semibold text-foreground">Update history</h3>
        {company.history.length ? (
          <Table>
            <TableHead><TableHeader>Field</TableHeader><TableHeader>Value</TableHeader><TableHeader>Source</TableHeader><TableHeader>Status</TableHeader><TableHeader>Observed</TableHeader></TableHead>
            <tbody>{company.history.map((entry) => (
              <TableRow key={`${entry.id}:${entry.field_key}`}>
                <TableCell>{entry.field_label}</TableCell>
                <TableCell className="max-w-[320px] break-words font-semibold">{formatValue(entry.value)}</TableCell>
                <TableCell>{entry.provider}</TableCell>
                <TableCell><Badge status={entry.lifecycle}>{entry.lifecycle}</Badge></TableCell>
                <TableCell className="whitespace-nowrap text-muted-foreground">{formatDate(entry.observed_at)}</TableCell>
              </TableRow>
            ))}</tbody>
          </Table>
        ) : <EmptyState title="No observations yet" description="This record exists in the catalog but no global field values have been captured." />}
      </section>
    </Panel>
  );
}
